import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type {
  UnifiedError,
  ErrorFilters,
  ErrorSummary,
  ErrorDistribution,
  ErrorSeverity,
} from "@/lib/error-types";

interface UseExtractionErrorsResult {
  errors: UnifiedError[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  summary: ErrorSummary | null;
  distribution: ErrorDistribution[];
  summaryLoading: boolean;
  distributionLoading: boolean;
  fetchErrors: (filters?: ErrorFilters, page?: number) => Promise<void>;
  fetchSummary: () => Promise<void>;
  fetchDistribution: (startDate?: Date, endDate?: Date) => Promise<void>;
}

// Map extraction_scope_event.level to ErrorSeverity
function mapLevelToSeverity(level: string): ErrorSeverity {
  switch (level.toLowerCase()) {
    case 'error':
      return 'error';
    case 'warning':
    case 'warn':
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Hook for fetching extraction errors from extraction_scope_event table
 */
export function useExtractionErrors(): UseExtractionErrorsResult {
  const [errors, setErrors] = useState<UnifiedError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [distribution, setDistribution] = useState<ErrorDistribution[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [distributionLoading, setDistributionLoading] = useState(false);

  const fetchErrors = useCallback(async (filters?: ErrorFilters, pageNum: number = 1) => {
    setLoading(true);
    try {
      let query = supabase
        .from('extraction_scope_event')
        .select(`
          id,
          code,
          level,
          message,
          created_at,
          reporting_run_id,
          section_token,
          structure_key,
          observation_id,
          finding_unit_id,
          atom_id,
          meta,
          reporting_run:reporting_run_id (
            user_id,
            exam_family_id
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Severity filter (maps to level column)
      if (filters?.severity === 'error') {
        query = query.eq('level', 'error');
      } else if (filters?.severity === 'warning') {
        query = query.in('level', ['warning', 'warn']);
      }
      // Otherwise include all levels

      // Date filters
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      // Code filter
      if (filters?.code) {
        query = query.eq('code', filters.code);
      }

      // Pagination
      query = query.range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      const { data, error, count } = await query;

      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.warn('extraction_scope_event table not found');
          setErrors([]);
          setTotal(0);
          setPage(pageNum);
          return;
        }
        throw error;
      }

      // Transform to UnifiedError format
      const unifiedErrors: UnifiedError[] = (data || []).map((row) => {
        const reportingRun = row.reporting_run as { user_id: string; exam_family_id: string } | null;

        return {
          id: row.id,
          source: 'extraction' as const,
          severity: mapLevelToSeverity(row.level),
          code: row.code,
          message: row.message,
          created_at: row.created_at,
          user_id: reportingRun?.user_id || null,
          user_email: null,
          context: {
            reporting_run_id: row.reporting_run_id,
            exam_family_id: reportingRun?.exam_family_id,
            section_token: row.section_token || undefined,
            structure_key: row.structure_key || undefined,
            observation_id: row.observation_id || undefined,
            finding_unit_id: row.finding_unit_id || undefined,
            atom_id: row.atom_id || undefined,
          },
          raw_payload: row.meta,
        };
      });

      setErrors(unifiedErrors);
      setTotal(count || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch extraction errors:', err);
      toast.error('Failed to load extraction events');
      setErrors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Get extraction event counts for 24h
      const { count: count24h } = await supabase
        .from('extraction_scope_event')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', yesterday.toISOString());

      // Get extraction event counts for 7d
      const { count: count7d } = await supabase
        .from('extraction_scope_event')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', lastWeek.toISOString());

      // Get recent events to find most common code
      const { data: recentEvents } = await supabase
        .from('extraction_scope_event')
        .select('code')
        .gte('created_at', lastWeek.toISOString())
        .limit(500);

      // Count codes
      const codeCounts: Record<string, number> = {};
      for (const row of recentEvents || []) {
        codeCounts[row.code] = (codeCounts[row.code] || 0) + 1;
      }

      let mostCommonCode: string | null = null;
      let mostCommonCount = 0;
      for (const [code, count] of Object.entries(codeCounts)) {
        if (count > mostCommonCount) {
          mostCommonCode = code;
          mostCommonCount = count;
        }
      }

      setSummary({
        total_24h: count24h || 0,
        total_7d: count7d || 0,
        error_rate_24h: 0,
        error_rate_7d: 0,
        most_common_code: mostCommonCode,
        most_common_count: mostCommonCount,
      });
    } catch (err) {
      console.error('Failed to fetch extraction summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchDistribution = useCallback(async (startDate?: Date, endDate?: Date) => {
    setDistributionLoading(true);
    try {
      let query = supabase
        .from('extraction_scope_event')
        .select('code');

      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data } = await query.limit(500);

      // Count codes
      const codeCounts: Record<string, number> = {};
      let totalCount = 0;

      for (const row of data || []) {
        codeCounts[row.code] = (codeCounts[row.code] || 0) + 1;
        totalCount++;
      }

      const dist: ErrorDistribution[] = Object.entries(codeCounts)
        .map(([code, count]) => ({
          code,
          count,
          percentage: totalCount > 0 ? (count / totalCount) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);

      setDistribution(dist);
    } catch (err) {
      console.error('Failed to fetch extraction distribution:', err);
      setDistribution([]);
    } finally {
      setDistributionLoading(false);
    }
  }, []);

  return {
    errors,
    total,
    page,
    pageSize,
    loading,
    summary,
    distribution,
    summaryLoading,
    distributionLoading,
    fetchErrors,
    fetchSummary,
    fetchDistribution,
  };
}
